/**
 * Centralized WebSocket connection manager with automatic reconnection.
 *
 * Use `WebSocketConnection` directly for non-React code (e.g. inside xterm effects).
 * Use `useWebSocket` hook for React components/hooks.
 */

import { isSessionExpired, useSessionStore } from '@/stores/session-store';

export interface WebSocketOptions {
  /** Full ws:// or wss:// URL, or a function that returns one. */
  url: string | (() => string);

  /** Called when a message arrives. */
  onMessage: (event: MessageEvent) => void;

  /** Called when the connection opens (including after reconnect). */
  onOpen?: () => void;

  /**
   * Called when the connection closes (before reconnect is scheduled).
   * NOT called during dispose().
   */
  onClose?: () => void;

  /**
   * Called on successful reconnect (not on initial connect).
   * Use this to re-sync state that may have been missed while disconnected.
   */
  onReconnect?: () => void;

  /** Set to 'arraybuffer' for binary protocols (e.g. terminal). */
  binaryType?: BinaryType;

  /** Max reconnect attempts. Default: Infinity (unlimited). */
  maxRetries?: number;

  /** Max backoff delay in ms. Default: 10000 (10s). */
  maxDelay?: number;

  /**
   * Called before each reconnect attempt. Return false to stop retrying.
   * Useful for checking if the sandbox is still in a connectable state.
   */
  shouldReconnect?: () => boolean;
}

/**
 * A connection must stay open at least this long before we treat it as healthy and reset the
 * backoff. Resetting on `onopen` alone means a server that accepts and immediately drops the
 * socket keeps backoff pinned at the minimum forever — a hot reconnect loop that looks like a
 * working connection to the retry logic.
 */
const STABLE_CONNECTION_MS = 5_000;

export class WebSocketConnection {
  private ws: WebSocket | null = null;
  private disposed = false;
  private retryCount = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private hasConnectedOnce = false;
  private openedAt = 0;
  private unsubscribeExpiry: (() => void) | null = null;
  private opts: Required<Pick<WebSocketOptions, 'maxRetries' | 'maxDelay'>> & WebSocketOptions;

  constructor(options: WebSocketOptions) {
    this.opts = {
      maxRetries: Infinity,
      maxDelay: 10_000,
      ...options,
    };
    // A handshake rejected with 401 closes as 1006 with no reason, so this class cannot
    // tell "logged out" from "wifi dropped" on its own and would retry the former
    // forever. The store carries that verdict over from the HTTP side.
    this.unsubscribeExpiry = useSessionStore.subscribe((state) => {
      if (state.expired) this.dispose();
    });
    this.connect();
  }

  /**
   * Send data over the WebSocket.
   *
   * Returns `false` if the socket isn't open, so callers can avoid recording optimistic state
   * for a message that never left the browser.
   */
  send(data: string | ArrayBufferLike | Blob | ArrayBufferView): boolean {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
      return true;
    }
    return false;
  }

  /** Current WebSocket readyState, or CLOSED if no socket exists. */
  get readyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED;
  }

  /** Permanently close the connection and stop reconnecting. */
  dispose(): void {
    this.disposed = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.unsubscribeExpiry?.();
    this.unsubscribeExpiry = null;
    this.ws?.close();
    this.ws = null;
  }

  private connect(): void {
    if (this.disposed || isSessionExpired()) return;

    const url = typeof this.opts.url === 'function' ? this.opts.url() : this.opts.url;
    const ws = new WebSocket(url);

    if (this.opts.binaryType) {
      ws.binaryType = this.opts.binaryType;
    }

    this.ws = ws;

    ws.onopen = () => {
      if (this.disposed) return;
      const isReconnect = this.hasConnectedOnce;
      this.openedAt = Date.now();
      this.hasConnectedOnce = true;
      this.opts.onOpen?.();
      if (isReconnect) {
        this.opts.onReconnect?.();
      }
    };

    ws.onmessage = (event) => {
      if (this.disposed) return;
      this.opts.onMessage(event);
    };

    ws.onclose = () => {
      if (this.disposed) return;
      // Only a connection that actually held up counts as success. See STABLE_CONNECTION_MS.
      if (this.openedAt && Date.now() - this.openedAt >= STABLE_CONNECTION_MS) {
        this.retryCount = 0;
      }
      this.openedAt = 0;
      this.opts.onClose?.();
      this.scheduleReconnect();
    };

    ws.onerror = () => {
      // onclose fires after onerror — reconnect handled there
    };
  }

  private scheduleReconnect(): void {
    if (this.disposed || isSessionExpired()) return;
    if (this.retryCount >= this.opts.maxRetries) return;
    if (this.opts.shouldReconnect && !this.opts.shouldReconnect()) return;

    // Full jitter: without it every socket on the page (chat, terminal, fs-watch) retries in
    // lockstep after a shared outage and lands on the server as a synchronized burst.
    const ceiling = Math.min(1000 * 2 ** this.retryCount, this.opts.maxDelay);
    const delay = ceiling / 2 + Math.random() * (ceiling / 2);
    this.retryCount += 1;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, delay);
  }
}

/** Build a ws:// or wss:// URL from a path, using the current page host. */
export function wsUrl(path: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}${path}`;
}
